import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { LinkButton } from "../../../components/LinkButton";
import { LoadingOverlay } from "../../../components/Loading";
import { Band, BandBooking, Vendor } from "../../../__generated__/graphql";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { ArtistLinkTypePicker } from "../../../components/ArtistLinkTypePicker";
import { BandPicker } from "../../../components/BandPicker";
import { VendorPicker } from "../../../components/VendorPicker";
import { UPDATE_BAND_LINKS } from "../graphql";
import { toast } from "react-toastify";
import { UPDATE_VENDOR_LINKS } from "../../vendors/vendor/graphql";
import { GET_VENDOR } from "../../vendors/graphql";
import { PageContainer } from "../../../components/PageContainer";

interface IVendorLinks {
  vendor: Vendor;
  disableContainer?: boolean;
}

export interface IArtistLink {
  type: "artist" | "vendor";
  linkedItemName: string;
  linkedId: string;
  description: string;
}

const schema = yup
  .object({
    links: yup
      .array()
      .of(
        yup.object({
          type: yup.string().required(),
          linkedItemName: yup.string().required(),
          linkedId: yup.string().required(),
          description: yup.string().required(),
        })
      )
      .required(),
  })
  .required();

const VendorLinksList = ({ vendor, disableContainer }: IVendorLinks) => {
  const links: IArtistLink[] =
    Object.keys(vendor.links ?? {}).map((key) => {
      const { type, linkedItemName, linkedId, description } = vendor.links[
        key
      ] as IArtistLink;

      return {
        type,
        linkedId,
        linkedItemName,
        description,
      };
    }) ?? [];

  if (!links) return null;

  const content = <VendorLinksForm vendor={vendor} links={links} />;

  if (disableContainer) {
    return content;
  }

  return <PageContainer title="Linked Items">{content}</PageContainer>;
};

export interface IVendorLinksform {
  vendor: Vendor;
  links: IArtistLink[];
}

const VendorLinksForm = ({ vendor, links }: IVendorLinksform) => {
  const [forDeletion, updateForDeletion] = useState<string[]>([]);
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, dirtyFields },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      links: links,
    },
  });

  const { fields, replace, append, remove, swap, move, insert } = useFieldArray(
    {
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "links", // unique name for your Field Array
    }
  );

  const [updateVendor, { loading: updateLoading, error }] = useMutation(
    UPDATE_VENDOR_LINKS,
    {
      onCompleted: () => {
        toast("Vendor updated");
      },
      refetchQueries: [GET_VENDOR],
    }
  );

  useEffect(() => {
    reset({
      links: links,
    });
  }, [links]);

  const onSubmit = (form: { links: IArtistLink[] }) => {
    const withoutDeleted = form.links.filter(
      (item) => !forDeletion.includes(item.linkedId)
    );

    const ready = withoutDeleted.reduce((acc, next) => {
      // @ts-ignore
      acc[next.linkedId] = next;

      return acc;
    }, {});

    updateVendor({
      variables: {
        vendor: {
          id: vendor.id,
          links: ready,
        },
      },
    });
  };

  return (
    // @ts-ignore
    <form onSubmit={handleSubmit(onSubmit)}>
      <Button
        onClick={() =>
          append({
            // @ts-ignore
            state: "unsaved",
            type: "artist",
            linkedItemName: "",
            linkedId: "",
            description: "",
          })
        }
        color="success"
        variant="contained"
        style={{ height: "100%", marginBottom: 8 }}
      >
        Add New
      </Button>
      <TableContainer component={Paper} style={{ minHeight: 500 }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Type</TableCell>
                <TableCell align="left">Linked Item</TableCell>
                <TableCell align="left">Description</TableCell>
                <TableCell align="left">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "& td": {
                      verticalAlign: "top",
                      py: 2,
                    },
                  }}
                  style={{ overflow: "visible" }}
                >
                  <input
                    type="hidden"
                    defaultValue={row.linkedId ?? ""}
                    {...register(`links.${index}.linkedId`)}
                  />
                  <TableCell align="left" style={{ overflow: "visible" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}
                    >
                      <Controller
                        control={control}
                        {...register(`links.${index}.type`)}
                        render={({ field: { onChange, value } }) => (
                          <ArtistLinkTypePicker
                            // @ts-ignore
                            handleChange={onChange}
                            defaultValue={value}
                            value={value}
                          />
                        )}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="left" style={{ overflow: "visible" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}
                    >
                      <Controller
                        control={control}
                        {...register(`links.${index}.linkedId`)}
                        render={({ field: { value } }) => {
                          const except = watch("links").map(
                            (item) => item.linkedId
                          );
                          return (
                            <>
                              {watch(`links.${index}.type`) === "artist" && (
                                <BandPicker
                                  except={except}
                                  controlStyle={{ marginTop: 0, marginBottom: 0 }}
                                  handleChange={(val, option) => {
                                    setValue(`links.${index}.linkedId`, val);

                                    setValue(
                                      `links.${index}.linkedItemName`,
                                      // @ts-ignore
                                      option.name
                                    );
                                  }}
                                  defaultValue={value}
                                  value={value}
                                />
                              )}

                              {watch(`links.${index}.type`) !== "artist" && (
                                <VendorPicker
                                  except={except}
                                  placeholder="Select Vendor"
                                  controlStyle={{ marginTop: 0, marginBottom: 0 }}
                                  handleChange={(val, option) => {
                                    setValue(`links.${index}.linkedId`, val);

                                    setValue(
                                      `links.${index}.linkedItemName`,
                                      // @ts-ignore
                                      option.name
                                    );
                                  }}
                                  defaultValue={value}
                                  value={value}
                                />
                              )}
                            </>
                          );
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    <Box
                      sx={{ display: "flex", alignItems: "flex-end", width: "100%" }}
                    >
                      <TextField
                        fullWidth
                        id="outlined-description"
                        label="Description"
                        variant="outlined"
                        InputProps={{
                          sx: { height: 56 },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        defaultValue={row.description}
                        error={!!errors.links?.[index]?.description?.message}
                        {...register(`links.${index}.description`)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="left">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        minHeight: 56,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        rowGap={1}
                      >
                        {row.linkedId && !dirtyFields.links && (
                          <LinkButton
                            path={
                              watch(`links.${index}.type`) === "artist"
                                ? `/band/${row.linkedId}`
                                : `/vendor/${row.linkedId}`
                            }
                            text="View"
                            variant="outlined"
                          />
                        )}
                        <Button
                          disabled={forDeletion.includes(row.linkedId)}
                          onClick={() => {
                            // @ts-ignore
                            if (row.state === "unsaved") {
                              remove(index);
                            } else {
                              updateForDeletion([...forDeletion, row.linkedId]);
                            }
                          }}
                          color="error"
                          variant="contained"
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {/* {links.map((row) => (
              <TableRow
                key={row.linkedId}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell align="left">{row.linkedItemName}</TableCell>
                <TableCell align="left">{row.type}</TableCell>
                <TableCell align="left">{row.description}</TableCell>
                <TableCell align="left">
                  <LinkButton
                    path={`/band/${band.id}/links/${row.linkedId}/edit`}
                    text="View"
                  />
                </TableCell>
              </TableRow>
            ))} */}
            </TableBody>
          </Table>
      </TableContainer>
      <Button
        type="submit"
        variant="contained"
        style={{ marginTop: 18, marginBottom: 18 }}
      >
        Save
      </Button>
    </form>
  );
};

export default VendorLinksList;
